import { parseInt10 } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import CourseReportPage from '@/ui/pages/trainings/CourseReportPage.vue';
import { guardRedirectEncodedId } from '@/ui/routes/guards/guardRedirectEncodedId';
import { guardSubscriptionPage } from '@/ui/routes/guards/guardSubscriptionPage';
import { Route } from 'vue-router';
import { NamedRouteConfig } from './types';
import { subscriptionRoutes } from '@/ui/routes/subscriptionRoutes';

import { ProfilePage } from '@/ui/pages/auth/ProfilePage';
import { IssueIndexPage } from '@/ui/pages/issue/IssueIndexPage';
import { IssueEditPage } from '@/ui/pages/issue/IssueEditPage';
import { CategoryEditPage } from '@/ui/pages/category/CategoryEditPage';
import { CategoryCreatePage } from '@/ui/pages/category/CategoryCreatePage';
import { EntityItemEditPage } from '@/ui/pages/entityItem/EntityItemEditPage';
import { EntityItemCreatePage } from '@/ui/pages/entityItem/EntityItemCreatePage';
import EntityTypeItemsPage from '@/ui/pages/entityItem/EntityTypeItemsPage.vue';
import { GroupCreatePage } from '@/ui/pages/group/GroupCreatePage';
import { GroupEditPage } from '@/ui/pages/group/GroupEditPage';
import { RoleCreatePage } from '@/ui/pages/role/RoleCreatePage';
import { RoleEditPage } from '@/ui/pages/role/RoleEditPage';
import { UserEditPage } from '@/ui/pages/user/UserEditPage';
import { UserCreatePage } from '@/ui/pages/user/UserCreatePage';
import InspectionDetailPage from '@/ui/pages/inspection/InspectionDetailPage.vue';
import { GatewayEditPage } from '../pages/gateway/GatewayEditPage';
import { SensorCreatePage } from '../pages/sensor/SensorCreatePage';
import { SensorEditPage } from '../pages/sensor/SensorEditPage';
import { InspectionFromTemplatePage } from '../pages/inspection/InspectionFromTemplatePage';
import { NotificationIndexPage } from '../pages/notification/NotificationIndexPage';
import { NotificationDetailPage } from '../pages/notification/NotificationDetailPage';
import { SensorMeasurementsPage } from '../pages/sensor/SensorMeasurementsPage';
import { IssueCreatePage } from '@/ui/pages/issue/IssueCreatePage';
import { OverdueInspectionsDetailPage } from '@/ui/pages/analytics/OverdueInspections/OverdueInspectionsDetailPage';
import { ScoresDetailPage } from '../pages/analytics/Scores/ScoresDetailPage';
import { NegativeResponsesDetailPage } from '../pages/analytics/NegativeResponses/NegativeResponsesDetailPage';
import { IssuesDetailPage } from '../pages/analytics/Issues/IssuesDetailPage';
import { SummaryChecklistsDetailPage } from '@/ui/pages/analytics/SummaryChecklists/SummaryChecklistsDetailPage';
import { SummaryChecklistsExplainPage } from '@/ui/pages/analytics/SummaryChecklists/SummaryChecklistExplainPage';
import { SummaryChecklistsPhotosPage } from '@/ui/pages/analytics/SummaryChecklists/SummaryChecklistPhotosPage';
import SubscriptionLayout from '@/ui/layouts/SubscriptionLayout';
import { FormsPage } from '@/ui/pages/forms/FormsIndexPage/FormsPage';
import AiPhotoValidationDetailTab from '@/ui/pages/forms/FormsIndexPage/tabs/AiPhotoValidationDetailTab.vue';
import { EntityFolderCreatePage } from '@/ui/pages/entityFolder/EntityFolderCreatePage';
import { EntityFolderEditPage } from '@/ui/pages/entityFolder/EntityFolderEditPage';
import { AdministrationPage } from '@/ui/pages/administration/AdministrationPage';
import { EntityItemsPage } from '@/ui/pages/entityItem/EntityItemsPage';
import { SensorsPage } from '@/ui/pages/sensor/SensorsPage';
import { AnalyticsPage } from '@/ui/pages/analytics/AnalyticsPage';
import IntegrationsPage from '@/ui/pages/integrations/ListingPage.vue';
import IntegrationDetailsPage from '@/ui/pages/integrations/DetailsPage.vue';
import IntegrationsOverviewTab from '@/ui/pages/integrations/OverviewTab.vue';
import IntegrationsLogsTab from '@/ui/pages/integrations/LogsTab.vue';
import { EntityTypeEditPage } from '@/ui/pages/entityType/EntityTypeEditPage';
import { EntityTypeCreatePage } from '@/ui/pages/entityType/EntityTypeCreatePage';
import AnalyticsIndexPage from '@/ui/pages/analytics/Builder/AnalyticsIndexPage.vue';
import HomePage from '@/ui/pages/home/HomePage.vue';
import { guardAnalyticsFeatureToggle } from '@/ui/routes/guards/guardAnalyticsFeatureToggle';
import { guardApprovalsFeatureToggle } from '@/ui/routes/guards/guardApprovalsFeatureToggle';
import ScheduleEditPage from '@/ui/pages/scheduleManager/ScheduleEditPage.vue';
import ScheduleCreatePage from '@/ui/pages/scheduleManager/ScheduleCreatePage.vue';
import ApprovalsIndexPage from '@/ui/pages/approvals/ApprovalsIndexPage.vue';
import { guardTrainingsModuleAddon } from '@/ui/routes/guards/guardTrainingsModuleAddon';
import TrainingPage from '@/ui/pages/trainings/TrainingPage.vue';

export const parseIdFromRoute = (route: Route) => ({ id: parseInt10(route.params.id) });

export const authenticatedRoutes: NamedRouteConfig[] = [
  {
    path: '/',
    name: 'homeNew',
    component: HomePage,
    meta: {
      title: t('common.home'),
    },
  },
  {
    path: '/approvals',
    name: 'approvalIndex',
    component: ApprovalsIndexPage,
    beforeEnter: guardApprovalsFeatureToggle,
    meta: {
      title: t('common.approvals'),
    },
  },
  {
    path: '/issues',
    name: 'issueIndex',
    component: IssueIndexPage,
    meta: {
      title: t('common.issues'),
    },
  },
  {
    path: '/issues/new',
    name: 'issueCreate',
    component: IssueCreatePage,
    meta: {
      title: t('common.issues'),
    },
  },
  {
    path: '/issues/:id',
    name: 'issueEdit',
    component: IssueEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.issues'),
    },
  },
  {
    path: '/categories/new',
    name: 'categoryCreate',
    component: CategoryCreatePage,
    meta: {
      title: t('common.categories'),
    },
  },
  {
    path: '/categories/:id',
    name: 'categoryEdit',
    component: CategoryEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.categories'),
    },
  },
  {
    path: '/entity-folders/new',
    name: 'entityFolderCreate',
    component: EntityFolderCreatePage,
    meta: {
      title: t('common.entity-folders'),
    },
  },
  {
    path: '/entity-folder/:id',
    name: 'entityFolderEdit',
    component: EntityFolderEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.entity-folders'),
    },
  },
  {
    path: '/entity-types/new',
    name: 'entityTypeCreate',
    component: EntityTypeCreatePage,
    meta: {
      title: t('common.entity-types'),
    },
  },
  {
    path: '/entity-types/:id',
    name: 'entityTypeEdit',
    component: EntityTypeEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.entity-types'),
    },
  },
  {
    path: '/entity-items/new',
    name: 'entityItemCreate',
    component: EntityItemCreatePage,
    meta: {
      title: t('common.entity-items'),
    },
  },
  {
    path: '/entity-items/:id',
    name: 'entityItemEdit',
    component: EntityItemEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.entity-items'),
    },
  },
  {
    path: '/entities/type/:id',
    name: 'entityTypeItems',
    component: EntityTypeItemsPage,
    props: (route: Route) => ({ typeId: parseInt10(route.params.id) }),
    meta: {
      title: t('common.entity-items'),
    },
  },
  {
    path: '/groups/new',
    name: 'groupCreate',
    component: GroupCreatePage,
    meta: {
      title: t('common.groups'),
    },
  },
  {
    path: 'groups/:id',
    name: 'groupEdit',
    component: GroupEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.groups'),
    },
  },
  {
    path: '/schedules/new',
    name: 'scheduleCreate',
    component: ScheduleCreatePage,
    meta: {
      title: t('common.schedules'),
    },
  },
  {
    path: 'schedules/:id',
    name: 'scheduleEdit',
    component: ScheduleEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.schedules'),
    },
  },
  {
    path: '/sensors/new',
    name: 'sensorCreate',
    component: SensorCreatePage,
    meta: {
      title: t('common.sensors'),
    },
  },
  {
    path: 'sensors/:id/measurements',
    name: 'sensorMeasurements',
    component: SensorMeasurementsPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.sensors'),
    },
  },
  {
    path: 'sensors/:id',
    name: 'sensorEdit',
    component: SensorEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.sensors'),
    },
  },
  {
    path: 'gateways/:id',
    name: 'gatewayEdit',
    component: GatewayEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.gateways'),
    },
  },
  {
    path: '/roles/new',
    name: 'roleCreate',
    component: RoleCreatePage,
    meta: {
      title: t('common.roles'),
    },
  },
  {
    path: 'roles/:id',
    name: 'roleEdit',
    component: RoleEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.roles'),
    },
  },
  {
    path: '/users/new',
    name: 'userCreate',
    component: UserCreatePage,
    meta: {
      title: t('common.users'),
    },
  },
  {
    path: 'users/:id',
    name: 'userEdit',
    component: UserEditPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.users'),
    },
  },
  {
    path: '/inspections/from-template',
    name: 'inspectionCreateFromTemplate',
    component: InspectionFromTemplatePage,
    meta: {
      title: t('common.inspections'),
    },
  },
  {
    path: '/inspections/public/:id',
    name: 'inspectionDetailPublic',
    beforeEnter: guardRedirectEncodedId,
  },
  {
    path: '/inspections/:id',
    name: 'inspectionDetail',
    component: InspectionDetailPage,
    props: (route: Route) => ({
      id: parseInt10(route.params.id),
      showConfetti: Boolean(route.params.showConfetti) || Boolean(route.query.showConfetti),
    }),
    meta: {
      title: t('common.inspections'),
    },
  },
  {
    path: '/inspections/:id/:section',
    name: 'inspectionDetailSection',
    component: InspectionDetailPage,
    props: (route: Route) => ({ id: parseInt10(route.params.id), section: route.params.section }),
    meta: {
      title: t('common.inspections'),
    },
  },
  {
    path: 'profile',
    name: 'profile',
    component: ProfilePage,
    meta: {
      title: t('common.profile'),
    },
  },
  {
    path: '/subscription',
    component: SubscriptionLayout,
    beforeEnter: guardSubscriptionPage,
    children: subscriptionRoutes,
  },
  {
    path: '/notifications',
    name: 'notificationIndex',
    component: NotificationIndexPage,
    meta: {
      title: t('common.notifications'),
    },
  },
  {
    path: '/notifications/:id',
    name: 'notificationDetail',
    component: NotificationDetailPage,
    props: (route: Route) => ({ id: route.params.id }),
    meta: {
      title: t('common.notifications'),
    },
  },
  {
    path: '/analytics/overdue-inspections/detail',
    name: 'analyticsOverdueInspectionsExplain',
    component: OverdueInspectionsDetailPage,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/scores/detail',
    name: 'analyticsScoresExplain',
    component: ScoresDetailPage,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/summary-checklists/detail/:id',
    name: 'analyticsSummaryChecklistsDetail',
    component: SummaryChecklistsDetailPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/summary-checklists/detail/:id/photos',
    name: 'analyticsSummaryChecklistsPhotos',
    component: SummaryChecklistsPhotosPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/summary-checklists/detail/:id/explain',
    name: 'analyticsSummaryChecklistsExplain',
    component: SummaryChecklistsExplainPage,
    props: parseIdFromRoute,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/negative-responses/detail',
    name: 'analyticsNegativeResponsesExplain',
    component: NegativeResponsesDetailPage,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/analytics/issues/detail',
    name: 'analyticsIssuesExplain',
    component: IssuesDetailPage,
    meta: {
      title: t('common.analytics'),
    },
  },
  {
    path: '/forms',
    name: 'forms',
    component: FormsPage,
  },
  {
    path: '/forms/ai-photo-validation/:id',
    name: 'aiPhotoValidationDetail',
    component: AiPhotoValidationDetailTab,
    props: parseIdFromRoute,
    meta: {
      title: t('ai-photo-validation.detail-title'),
    },
  },
  {
    path: '/administration',
    name: 'administration',
    component: AdministrationPage,
  },
  {
    path: '/entities',
    name: 'entityItems',
    component: EntityItemsPage,
  },
  {
    path: '/sensors',
    name: 'sensors',
    component: SensorsPage,
  },
  {
    path: '/analytics-legacy',
    name: 'analytics',
    component: AnalyticsPage,
  },
  {
    path: '/analytics',
    name: 'analytics-v2',
    component: AnalyticsIndexPage,
    beforeEnter: guardAnalyticsFeatureToggle,
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: IntegrationsPage,
  },
  {
    path: '/integrations/:id',
    name: 'integrationDetails',
    component: IntegrationDetailsPage,
    props: true,
    redirect: { name: 'integrationDetailsOverview' },
    children: [
      {
        path: 'overview',
        name: 'integrationDetailsOverview',
        component: IntegrationsOverviewTab,
      },
      {
        path: 'logs',
        name: 'integrationDetailsLogs',
        component: IntegrationsLogsTab,
      },
    ],
  },
  {
    path: '/courses',
    name: 'courses',
    component: TrainingPage,
    beforeEnter: guardTrainingsModuleAddon,
    meta: {
      title: t('common.training'),
    },
  },
  {
    path: '/course-reports/:id',
    name: 'courseReport',
    component: CourseReportPage,
    props: parseIdFromRoute,
    beforeEnter: guardTrainingsModuleAddon,
    meta: {
      title: t('inspections.report'),
    },
  },
];
